if (!customElements.get('mr-size-overlay-picker')) {
  class MRSizeOverlayPicker extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.selectedVariantId = 0;
      this.selectedSize = '';
      this.selectedVariant = null;
      this.selectedBand = '';
      this.selectedCup = '';

      this.handleClick = this.handleClick.bind(this);
      this.handleEscape = this.handleEscape.bind(this);

      this.addEventListener('click', this.handleClick);
      document.addEventListener('keydown', this.handleEscape);

      this.refreshStateFromDom();
      this.syncInitialState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('keydown', this.handleEscape);
      this._connected = false;
    }

    refreshStateFromDom() {
      this.overlay = this.querySelector('.mr-size-overlay');
      this.openBtn = this.querySelector('[data-open-size-overlay]');
      this.addToCartBtn = this.querySelector('[data-size-add-to-cart]');
      this.triggerValue = this.querySelector('[data-size-trigger-value]');
      this.hiddenSizeInput = this.querySelector('[data-size-input]');

      this.sizeButtons = Array.from(this.querySelectorAll('[data-size-value]'));
      this.bandButtons = Array.from(this.querySelectorAll('[data-band-value]'));
      this.cupButtons = Array.from(this.querySelectorAll('[data-cup-value]'));

      this.sizeOptionIndex = Number.parseInt(this.dataset.sizeOptionIndex ?? '1', 10);
      this.context = this.dataset.context || 'card';
      this.sizeMode = this.dataset.sizeMode || 'standard';
      this.productId = String(this.dataset.productId || '');
      this.sectionId = String(this.dataset.sectionId || '');

      // Hard reset every time from current DOM/dataset
      this.selectedVariantId = Number(this.dataset.selectedVariantId || 0);
      this.selectedSize = this.dataset.selectedSize || '';
      this.selectedVariant = null;
      this.selectedBand = '';
      this.selectedCup = '';

      const variantDataEl = this.querySelector('[data-product-variants-json]');
      this.variants = [];

      if (variantDataEl) {
        try {
          this.variants = JSON.parse(variantDataEl.textContent);
        } catch (error) {
          console.error('MR size overlay: could not parse variants JSON', error);
        }
      }

      if (this.selectedVariantId) {
        this.selectedVariant =
          this.variants.find((variant) => Number(variant.id) === Number(this.selectedVariantId)) || null;
      }

      if (!this.selectedVariant && this.selectedSize) {
        this.selectedVariant =
          this.variants.find((variant) => this.getSizeValueFromVariant(variant) === this.selectedSize) || null;

        if (this.selectedVariant) {
          this.selectedVariantId = Number(this.selectedVariant.id);
        }
      }
    }

    handleClick(event) {
      const openBtn = event.target.closest('[data-open-size-overlay]');
      if (openBtn && this.contains(openBtn)) {
        event.preventDefault();
        event.stopPropagation();
        this.refreshStateFromDom();
        this.syncInitialState();
        this.openOverlay();
        return;
      }

      const closeBtn = event.target.closest('[data-close-size-overlay]');
      if (closeBtn && this.contains(closeBtn)) {
        event.preventDefault();
        event.stopPropagation();
        this.closeOverlay();
        return;
      }

      const sizeBtn = event.target.closest('[data-size-value]');
      if (sizeBtn && this.contains(sizeBtn)) {
        event.preventDefault();
        event.stopPropagation();
        this.selectSize(sizeBtn);
        return;
      }

      const bandBtn = event.target.closest('[data-band-value]');
      if (bandBtn && this.contains(bandBtn)) {
        event.preventDefault();
        event.stopPropagation();
        this.selectBand(bandBtn);
        return;
      }

      const cupBtn = event.target.closest('[data-cup-value]');
      if (cupBtn && this.contains(cupBtn)) {
        event.preventDefault();
        event.stopPropagation();
        this.selectCup(cupBtn);
        return;
      }

      const addBtn = event.target.closest('[data-size-add-to-cart]');
      if (addBtn && this.contains(addBtn)) {
        event.preventDefault();
        event.stopPropagation();
        this.addToCart();
      }
    }

    handleEscape(event) {
      if (event.key === 'Escape' && this.overlay && !this.overlay.hidden) {
        this.closeOverlay();
      }
    }

    syncInitialState() {
      if (this.sizeMode === 'one_size') {
        this.setupOneSize();
        return;
      }

      if (this.sizeMode === 'standard') {
        this.setupStandardSizes();
        return;
      }

      if (this.sizeMode === 'bra_split') {
        this.setupBraSplit();
      }
    }

    setupOneSize() {
      const matchedVariant =
        this.selectedVariant ||
        this.variants.find((variant) => variant.available) ||
        null;

      if (matchedVariant) {
        this.selectedVariant = matchedVariant;
        this.selectedVariantId = Number(matchedVariant.id);
        this.selectedSize = this.getSizeValueFromVariant(matchedVariant);
      }

      this.dataset.selectedVariantId = this.selectedVariantId ? String(this.selectedVariantId) : '';
      this.dataset.selectedSize = this.selectedSize || '';

      this.updateHiddenSizeInput(this.selectedSize);
      this.updateTriggerLabel(this.selectedSize);
      this.updateAddToCartState();
      this.syncProductFormVariantId();
    }

    setupStandardSizes() {
      if (this.selectedVariant) {
        this.selectedSize = this.getSizeValueFromVariant(this.selectedVariant);
      }

      this.updateActiveButtonBySize(this.selectedSize);
      this.updateHiddenSizeInput(this.selectedSize);
      this.updateTriggerLabel(this.selectedSize);
      this.updateAddToCartState();
      this.syncProductFormVariantId();
    }

    setupBraSplit() {
      const sourceSize =
        this.selectedSize ||
        this.hiddenSizeInput?.value ||
        this.getSizeValueFromVariant(this.selectedVariant) ||
        '';

      if (sourceSize && sourceSize.includes('/')) {
        const parts = sourceSize.split('/');
        this.selectedBand = (parts[0] || '').trim();
        this.selectedCup = (parts[1] || '').trim();
      }

      this.updateBandButtons();
      this.updateCupAvailability();
      this.updateBraUI();
      this.updateHiddenSizeInput(sourceSize);
      this.updateTriggerLabel(sourceSize);
      this.updateAddToCartState();
      this.syncProductFormVariantId();
    }

    openOverlay() {
      if (!this.overlay) return;
      this.overlay.hidden = false;
      this.openBtn?.setAttribute('aria-expanded', 'true');

      if (this.context !== 'card') {
        document.documentElement.classList.add('mr-size-overlay-open');
      }
    }

    closeOverlay() {
      if (!this.overlay) return;
      this.overlay.hidden = true;
      this.openBtn?.setAttribute('aria-expanded', 'false');

      if (this.context !== 'card') {
        document.documentElement.classList.remove('mr-size-overlay-open');
      }

      this.selectedVariant = null;
      this.selectedBand = '';
      this.selectedCup = '';
    }

    selectSize(button) {
      if (!button || button.disabled) return;

      const variantId = Number(button.dataset.variantId || 0);
      const sizeValue = button.dataset.sizeValue || '';

      if (!variantId || !sizeValue) return;

      const matchedVariant =
        this.variants.find((variant) => Number(variant.id) === variantId) || null;

      if (!matchedVariant || matchedVariant.available !== true) return;

      this.selectedVariant = matchedVariant;
      this.selectedVariantId = variantId;
      this.selectedSize = sizeValue;

      this.dataset.selectedVariantId = String(this.selectedVariantId);
      this.dataset.selectedSize = this.selectedSize;

      this.updateActiveButton(button);
      this.updateHiddenSizeInput(this.selectedSize);
      this.updateTriggerLabel(this.selectedSize);
      this.updateAddToCartState();
      this.syncProductFormVariantId();
    }

    selectBand(button) {
      if (!button || button.disabled) return;

      this.selectedBand = button.dataset.bandValue || '';
      this.updateBandButtons();
      this.updateCupAvailability();

      const selectedCupButton =
        this.querySelector('[data-cup-value].is-selected, [data-cup-value].is-active') ||
        this.cupButtons.find((btn) => !btn.disabled);

      if (selectedCupButton) {
        this.selectCup(selectedCupButton);
      } else {
        this.selectedVariant = null;
        this.selectedVariantId = 0;
        this.selectedSize = '';
        this.updateHiddenSizeInput('');
        this.updateTriggerLabel('');
        this.updateAddToCartState();
      }
    }

    selectCup(button) {
      if (!button || button.disabled) return;

      this.selectedCup = button.dataset.cupValue || '';
      this.updateBraUI();

      const fullSize = `${this.selectedBand}/${this.selectedCup}`;
      const matchedVariant =
        this.variants.find((variant) => this.getSizeValueFromVariant(variant) === fullSize) || null;

      if (!matchedVariant || matchedVariant.available !== true) {
        this.selectedVariant = null;
        this.selectedVariantId = 0;
        this.selectedSize = fullSize;
        this.updateHiddenSizeInput(this.selectedSize);
        this.updateTriggerLabel(this.selectedSize);
        this.updateAddToCartState();
        return;
      }

      this.selectedVariant = matchedVariant;
      this.selectedVariantId = Number(matchedVariant.id);
      this.selectedSize = fullSize;

      this.dataset.selectedVariantId = String(this.selectedVariantId);
      this.dataset.selectedSize = this.selectedSize;

      this.updateHiddenSizeInput(this.selectedSize);
      this.updateTriggerLabel(this.selectedSize);
      this.updateAddToCartState();
      this.syncProductFormVariantId();
    }

    updateBandButtons() {
      this.bandButtons.forEach((btn) => {
        const isSelected = btn.dataset.bandValue === this.selectedBand;
        btn.classList.toggle('is-active', isSelected);
        btn.classList.toggle('is-selected', isSelected);
      });
    }

    updateCupAvailability() {
      this.cupButtons.forEach((btn) => {
        const cupValue = btn.dataset.cupValue || '';

        const hasAvailableVariant = this.variants.some((variant) => {
          if (!variant.available) return false;

          const sizeValue = this.getSizeValueFromVariant(variant);
          if (!sizeValue || !sizeValue.includes('/')) return false;

          const [band, cup] = sizeValue.split('/').map((part) => (part || '').trim());
          return band === this.selectedBand && cup === cupValue;
        });

        btn.disabled = !hasAvailableVariant;
        btn.setAttribute('aria-disabled', hasAvailableVariant ? 'false' : 'true');
        btn.classList.toggle('is-disabled', !hasAvailableVariant);
      });
    }

    updateBraUI() {
      this.updateBandButtons();

      this.cupButtons.forEach((btn) => {
        const isSelected = btn.dataset.cupValue === this.selectedCup;
        btn.classList.toggle('is-active', isSelected);
        btn.classList.toggle('is-selected', isSelected);
      });
    }

    updateActiveButton(activeButton) {
      this.sizeButtons.forEach((btn) => {
        btn.classList.remove('is-active', 'is-selected');
      });

      if (activeButton) {
        activeButton.classList.add('is-active', 'is-selected');
      }
    }

    updateActiveButtonBySize(sizeValue) {
      const matchedButton = this.sizeButtons.find((btn) => btn.dataset.sizeValue === sizeValue);
      this.updateActiveButton(matchedButton || null);
    }

    updateTriggerLabel(value) {
      if (!this.triggerValue) return;
      this.triggerValue.textContent = value || 'Select Size';
    }

    updateHiddenSizeInput(value) {
      if (!this.hiddenSizeInput) return;
      this.hiddenSizeInput.value = value || '';
    }

    updateAddToCartState() {
      if (!this.addToCartBtn) return;

      const canAdd =
        this.selectedVariant &&
        Number(this.selectedVariant.id) > 0 &&
        this.selectedVariant.available === true;

      this.addToCartBtn.disabled = !canAdd;
    }

    getSizeValueFromVariant(variant) {
      if (!variant) return '';

      switch (this.sizeOptionIndex) {
        case 1:
          return variant.option1 || '';
        case 2:
          return variant.option2 || '';
        case 3:
          return variant.option3 || '';
        default:
          return variant.option1 || '';
      }
    }

    getProductFormComponent() {
      const productId = String(this.productId || '');

      const scopedRoots = [
        this.closest('quick-add-dialog'),
        this.closest('#quick-add-modal-content'),
        document.getElementById('quick-add-modal-content'),
        this.closest('.quick-add-modal'),
        this.closest('.shopify-section, [id^="shopify-section-"]'),
        document,
      ].filter(Boolean);

      for (const root of scopedRoots) {
        if (!root || !root.querySelector) continue;

        if (productId) {
          const exactMatch = root.querySelector(`product-form-component[data-product-id="${productId}"]`);
          if (exactMatch) return exactMatch;
        }

        const fallback = root.querySelector('product-form-component');
        if (fallback) return fallback;
      }

      return null;
    }

    getProductForm() {
      const component = this.getProductFormComponent();
      if (!component) return null;

      return (
        component.querySelector('form[data-type="add-to-cart-form"]') ||
        component.querySelector('form[action*="/cart/add"]') ||
        component.querySelector('form')
      );
    }

    getProductFormVariantInput() {
      const form = this.getProductForm();
      if (!form) return null;

      return form.querySelector('input[name="id"]');
    }

    syncProductFormVariantId() {
      if (!this.selectedVariantId) return;

      const hiddenInput = this.getProductFormVariantInput();
      if (!hiddenInput) {
        console.warn('MR size overlay: could not find product form variant input', {
          productId: this.productId,
          sectionId: this.sectionId,
        });
        return;
      }

      const nextValue = String(this.selectedVariantId);
      hiddenInput.value = nextValue;
      hiddenInput.removeAttribute('disabled');

      hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
      hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    async addToCart() {
  if (!this.selectedVariantId) return;

  const button = this.addToCartBtn;

  if (button?.dataset.mrSilentAdding === 'true') return;

  button.dataset.mrSilentAdding = 'true';
  button.setAttribute('aria-busy', 'true');
  button.classList.add('mr-silent-add-is-loading');

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: Number(this.selectedVariantId),
        quantity: 1
      })
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('[Main Rose] Size overlay add to cart error:', result);
      throw new Error(result?.description || result?.message || 'Could not add to cart');
    }

    const cartResponse = await fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (cartResponse.ok) {
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              itemCount: cart.item_count,
              source: 'main-rose-size-overlay'
            },
            cart
          }
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { cart }
        })
      );

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart }
        })
      );
    }

    const originalText = button.textContent;
    button.textContent = 'Added';
    button.classList.add('mr-silent-add-is-added');

    window.setTimeout(() => {
      button.textContent = originalText;
      button.classList.remove('mr-silent-add-is-added');
    }, 1200);

  } catch (error) {
    console.error('[Main Rose] Size overlay silent add failed:', error);
  } finally {
    delete button.dataset.mrSilentAdding;
    button.removeAttribute('aria-busy');
    button.classList.remove('mr-silent-add-is-loading');
  }
}
  }

  customElements.define('mr-size-overlay-picker', MRSizeOverlayPicker);
}